import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiBriefcase, FiBook, FiAward, FiUsers, FiMapPin, FiCalendar, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import './Journey.css';

const experienceData = [
  {
    id: 'exp-1',
    title: 'Insight360',
    org: 'ABB India Limited',
    location: 'Nashik, Maharashtra',
    period: 'August 2025 - November 2025',
    description:
      'INSIGHT360 is a digital platform that automates engineering calculations, standardizes workflows, and provides real-time visibility to improve efficiency, accuracy, and compliance within ABB.',
    highlights: [
      'Automated engineering calculations and standardized workflows.',
      'Provided real-time visibility to improve efficiency and accuracy.',
      'Ensured compliance with industry standards within ABB.',
    ],
    technologies: ['Power Apps', 'Power Automate', 'Sharepoint', 'Onedrive'],
    image: 'https://res.cloudinary.com/djnqexy8g/image/upload/v1771155462/abb_hfhs7y.jpg',
  },
];

const educationData = [
  {
    id: 'edu-1',
    title: 'B.Tech - Computer Engineering',
    org: 'K K Wagh Institute of Engineering Education and Research, Nashik',
    score: 'CGPA: 9.41/10',
    period: '2023 - 2027',
  },
  {
    id: 'edu-2',
    title: 'Higher Secondary (XII)',
    org: 'P E School and Aarambh Mahavidyalaya, Nashik',
    score: 'Percentage: 84.16%',
    period: '2023',
  },
  {
    id: 'edu-3',
    title: 'Secondary (X)',
    org: 'Scottish Academy High School, Nashik',
    score: 'Percentage: 97.60%',
    period: '2021',
  },
];

const certificationsData = [
  {
    id: 'cert-1',
    title: 'AWS Academy Graduate - Cloud Foundations',
    org: 'AWS Academy',
    period: 'March 2026',
    skills: 'Cloud Foundations',
    link: 'https://www.credly.com/badges/96c81520-7b71-4bd0-96f3-9af8ac35da58/print'
  },
  {
    id: 'cert-2',
    title: 'AWS Certified Alexa Skill Builder - Specialty',
    org: 'AWS',
    period: 'January 2026',
    skills: 'Generative AI',
    credentialId: 'c483d542-80d1-471b-9d3d-fad15b3fcf2d',
    link: 'https://drive.google.com/file/d/18PTbaYG0SbDpDXF24beMQfpBRC3In5yu/view?usp=sharing'
  },
  {
    id: 'cert-4',
    title: 'Database Management System',
    org: 'NPTEL',
    period: 'September 2025',
    skills: 'Database Management System (DBMS)',
    credentialId: 'NPTEL25CS145S54360098609168413',
    link: 'https://archive.nptel.ac.in/noc/Ecertificate/?q=NPTEL25CS145S54360098609168413'
  },
  {
    id: 'cert-5',
    title: 'The Complete Full-Stack Web Development Course',
    org: 'Udemy',
    period: 'March 2026',
    skills:'Full Stack Web Development',
    credentialId:'UC-e4dbcb65-2d88-4ee3-9e9a-d92c74fcbf99',
    link:'https://www.udemy.com/certificate/UC-e4dbcb65-2d88-4ee3-9e9a-d92c74fcbf99/'
  },
  {
    id: 'cert-7',
    title: 'iLike Certificate in Multi-Platform Digital Marketing Mastery: Strategy, Analytics and Execution',
    org: 'MKCL',
    period: 'March 2025',
  },
];

const CERT_INITIAL = 2;

const positionsData = [
  {
    id: 'pos-1',
    title: 'Head of Curation',
    org: 'E-Cell, KKWIEER',
    period: 'August 2025 - Present',
    description:
      'Led the curation and execution of events from concept to completion, overseeing planning, documentation, creative direction, logistics, and post-event reviews. Coordinated cross-functional teams and ensured seamless experiences through strategic planning and hands-on leadership.'
  },
  {
    id: 'pos-2',
    title: 'Core Committee Member',
    org: 'MIBCS, KKWIEER',
    period: 'Aug 2024 - Aug 2025',
    description:
      'Designed visual assets including posters, flyers, certificates, and broadcast creatives while contributing to the planning and execution of events. Collaborated across teams, managed responsibilities efficiently, and gained hands-on experience in coordination and creative communication.',
  },
];

const TimelineItem = ({ children, index, isLast }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <motion.div
      ref={ref}
      className="tl-item"
      initial={{ opacity: 0, x: -20 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <div className="tl-marker-col">
        <div className="tl-dot" />
        {!isLast && <div className="tl-line" />}
      </div>
      <div className="tl-content">
        {children}
      </div>
    </motion.div>
  );
};

const Journey = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [showAllCerts, setShowAllCerts] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <section className="journey-section" id="journey">
      <div className="section-container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
           Learning along<br /><span className="journey-title-light">the Way</span>
          </motion.h2>

          {/* Experience */}
          <motion.div className="journey-block" variants={itemVariants}>
            <div className="journey-block-header">
              <FiBriefcase className="journey-block-icon" />
              <h3 className="journey-block-title">Experience</h3>
            </div>

            <div className="tl-timeline">
              {experienceData.map((item, i) => (
                <TimelineItem key={item.id} index={i} isLast={i === experienceData.length - 1}>
                  <div className="tl-card tl-card--exp">
                    <div className="tl-card-inner">
                      <div className="tl-card-text">
                        <div className="tl-card-header">
                          <div className="tl-card-company-info">
                            <div className="tl-card-icon-wrap">
                              <FiBriefcase />
                            </div>
                            <div>
                              <h4 className="tl-card-org">{item.org}</h4>
                              <p className="tl-card-role">{item.title}</p>
                            </div>
                          </div>
                          <div className="tl-card-meta">
                            <span className="tl-card-period-badge">
                              <FiCalendar />
                              {item.period}
                            </span>
                            {item.location && (
                              <span className="tl-card-location">
                                <FiMapPin />
                                {item.location}
                              </span>
                            )}
                          </div>
                        </div>

                        <p className="tl-card-desc">{item.description}</p>

                        {item.highlights && (
                          <ul className="tl-card-highlights">
                            {item.highlights.map((h, idx) => (
                              <li key={idx}>{h}</li>
                            ))}
                          </ul>
                        )}

                        {item.technologies && (
                          <div className="tl-card-tech-tags">
                            {item.technologies.map((tech, idx) => (
                              <span key={idx} className="tl-card-tech-tag">{tech}</span>
                            ))}
                          </div>
                        )}
                      </div>

                      {item.image && (
                        <div className="tl-card-image-side">
                          <div className="tl-card-image-frame">
                            <img
                              src={item.image}
                              alt={item.org}
                              className="tl-card-image"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </TimelineItem>
              ))}
            </div>
          </motion.div>

          {/* Education */}
          <motion.div className="journey-block" variants={itemVariants}>
            <div className="journey-block-header">
              <FiBook className="journey-block-icon" />
              <h3 className="journey-block-title">Education</h3>
            </div>

            <div className="tl-timeline">
              {educationData.map((item, i) => (
                <TimelineItem key={item.id} index={i} isLast={i === educationData.length - 1}>
                  <div className="tl-card">
                    <div className="tl-card-header">
                      <div>
                        <h4 className="tl-card-org">{item.org}</h4>
                        <p className="tl-card-role">{item.title}</p>
                      </div>
                    </div>
                    {item.score && <p className="tl-card-score">{item.score}</p>}
                    {item.period && <span className="tl-card-period">{item.period}</span>}
                  </div>
                </TimelineItem>
              ))}
            </div>
          </motion.div>

          {/* Certifications */}
          <motion.div className="journey-block" variants={itemVariants}>
            <div className="journey-block-header">
              <FiAward className="journey-block-icon" />
              <h3 className="journey-block-title">Certifications</h3>
            </div>

            <div className="cert-grid">
              <AnimatePresence>
                {(showAllCerts ? certificationsData : certificationsData.slice(0, CERT_INITIAL)).map((item, i) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    whileHover={{ y: -4 }}
                    style={{ display: 'flex' }}
                  >
                    {item.link ? (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="cert-card" style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'block' }}>
                        <h4 className="cert-card-title">{item.title}</h4>
                        <p className="cert-card-org">{item.org}</p>
                        {item.period && <span className="cert-card-period">{item.period}</span>}
                        {item.credentialId && <p className="cert-card-credential" style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.5rem' }}>Credential ID: {item.credentialId}</p>}
                        {item.skills && <p className="cert-card-skills" style={{ fontSize: '0.85rem', color: 'var(--primary-color)', marginTop: '0.2rem' }}>Skills: {item.skills}</p>}
                      </a>
                    ) : (
                      <div className="cert-card" style={{ width: '100%' }}>
                        <h4 className="cert-card-title">{item.title}</h4>
                        <p className="cert-card-org">{item.org}</p>
                        {item.period && <span className="cert-card-period">{item.period}</span>}
                        {item.credentialId && <p className="cert-card-credential" style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.5rem' }}>Credential ID: {item.credentialId}</p>}
                        {item.skills && <p className="cert-card-skills" style={{ fontSize: '0.85rem', color: 'var(--primary-color)', marginTop: '0.2rem' }}>Skills: {item.skills}</p>}
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {certificationsData.length > CERT_INITIAL && (
              <div className="view-toggle-wrap">
                <motion.button
                  className="btn btn-secondary view-toggle-btn"
                  onClick={() => setShowAllCerts(!showAllCerts)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {showAllCerts ? <FiChevronUp /> : <FiChevronDown />}
                  {showAllCerts ? 'View Less' : 'View More'}
                </motion.button>
              </div>
            )}
          </motion.div>

          {/* Positions of Responsibility */}
          <motion.div className="journey-block" variants={itemVariants}>
            <div className="journey-block-header">
              <FiUsers className="journey-block-icon" />
              <h3 className="journey-block-title">Involvement</h3>
            </div>

            <div className="tl-timeline">
              {positionsData.map((item, i) => (
                <TimelineItem key={item.id} index={i} isLast={i === positionsData.length - 1}>
                  <div className="tl-card">
                    <div className="tl-card-header">
                      <div>
                        <h4 className="tl-card-org">{item.org}</h4>
                        <p className="tl-card-role">{item.title}</p>
                      </div>
                      <div className="tl-card-meta">
                        {item.period && <span className="tl-card-period">{item.period}</span>}
                      </div>
                    </div>
                    <p className="tl-card-desc">{item.description}</p>
                  </div>
                </TimelineItem>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Journey;
